/* Surprise machine (sections/kc-blind-box.liquid), the gift finder page's
   gachapon in the kit. Turn (the button, or a click on the machine): the
   knob spins, the key winds and the capsules rattle (1.05s), a capsule in
   a random pair of colours tumbles out of the chute (.95s), then the prize
   slot beside the machine swaps its "?" for a product with an add button.

   The prize comes from the machine's whole collection: kawaii-catalog.js
   fetches its feed as the section comes near and draws a product this
   shopper hasn't had from the machine lately (the memory outlives the
   visit), keeping the cards beside the machine out while there are
   others. If the feed can't be reached, the prize is one of those cards
   instead. Reduced motion skips straight to the prize. */
if (!customElements.get('kc-gacha')) {
  const TURN_MS = 1050;
  const DROP_MS = 950;
  const COLORS = ['#ff8cbb', '#ffd166', '#a7e0ff', '#c3f2cb', '#d7c4ff', '#ffb38a'];

  class KcGacha extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      } catch (e) {
        this.labels = {};
      }
      this.feed = this.dataset.feed || '';
      this.ready = true;
      this.busy = false;
      this.querySelectorAll('[data-turn]').forEach((el) => {
        el.addEventListener('click', () => this.turn());
      });
      this.preloadWhenNear();
    }

    disconnectedCallback() {
      clearTimeout(this.t1);
      clearTimeout(this.t2);
    }

    // Fetch the collection once the section is about to be seen, after the
    // page has loaded, so the first turn doesn't wait on it.
    preloadWhenNear() {
      if (!this.feed || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          const start = () => this.pool();
          if (document.readyState === 'complete') (window.requestIdleCallback || setTimeout)(start);
          else window.addEventListener('load', start, { once: true });
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(this);
    }

    // Every in-stock product with a photo in the collection; the cards
    // beside the machine when the feed can't be had (the next turn tries
    // the feed again).
    pool() {
      if (!this.request) {
        const catalog = window.kcCatalog;
        const load = catalog && this.feed ? catalog.load(this.feed) : Promise.reject(new Error('Surprise machine: no feed'));
        this.request = load
          .then((products) => {
            const list = products.filter((p) => p.image);
            if (!list.length) throw new Error('Surprise machine: empty feed');
            return list;
          })
          .catch(() => {
            this.request = null;
            return this.fromCards();
          });
      }
      return this.request;
    }

    // The cards beside the machine, in the feed's shape. Their price text
    // already says "from" when the price varies.
    fromCards() {
      const section = this.closest('.kc-bb') || this.parentElement;
      return Array.from(section.querySelectorAll('.kc-bb__card'), (card) => {
        const title = card.querySelector('.kc-card__title');
        const img = card.querySelector('.kc-card__media img');
        const price = card.querySelector('.kc-vh');
        const add = card.querySelector('add-to-cart[data-variant-id]');
        return {
          title: title ? title.textContent.trim() : '',
          url: title ? title.getAttribute('href') : '',
          price_label: price ? price.textContent.trim() : '',
          image: img ? img.currentSrc || img.src : '',
          variant: add ? add.dataset.variantId : null,
          single: Boolean(add)
        };
      }).filter((item) => item.url && item.image);
    }

    // The cards beside the machine, so the prize isn't one of them.
    onScreen() {
      const section = this.closest('.kc-bb') || this.parentElement;
      return Array.from(section.querySelectorAll('.kc-bb__card .kc-card__title'), (link) => link.getAttribute('href'));
    }

    shuffle(list) {
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
      return list;
    }

    show(state) {
      this.querySelector('[data-state-idle]').hidden = state !== 'idle';
      this.querySelector('[data-state-wait]').hidden = state !== 'wait';
      this.querySelector('[data-state-open]').hidden = state !== 'open';
    }

    turn() {
      if (this.busy) return;
      this.busy = true;
      const pool = this.pool();
      const quick = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const [a, b] = this.shuffle(COLORS.slice());
      this.style.setProperty('--drop-a', a);
      this.style.setProperty('--drop-b', b);
      this.classList.remove('is-dropping', 'is-open');
      this.show('wait');

      const open = () => {
        pool.then((list) => {
          if (list.length) {
            this.classList.add('is-open');
            this.reveal(list);
          } else {
            this.show('idle');
          }
          this.busy = false;
        });
      };

      if (quick) {
        this.classList.add('is-dropping');
        open();
        return;
      }
      // Restart the animations even if the last capsule is still lying there.
      void this.offsetWidth;
      this.classList.add('is-turning');
      this.t1 = setTimeout(() => {
        this.classList.remove('is-turning');
        this.classList.add('is-dropping');
      }, TURN_MS);
      this.t2 = setTimeout(open, TURN_MS + DROP_MS);
    }

    reveal(list) {
      const catalog = window.kcCatalog;
      const item = catalog
        ? catalog.draw(`machine:${this.feed}`, list, 1, this.onScreen())[0]
        : list[Math.floor(Math.random() * list.length)];

      const card = this.querySelector('template[data-prize]').content.firstElementChild.cloneNode(true);
      card.querySelector('[data-img]').src = catalog ? catalog.sized(item.image, 400) : item.image;
      card.querySelector('[data-link]').href = item.url;
      const name = card.querySelector('[data-name]');
      name.href = item.url;
      name.textContent = item.title;
      card.querySelector('[data-price]').textContent = (item.varies ? 'from ' : '') + item.price_label;
      card.querySelector('[data-add]').replaceWith(this.addButton(item));

      const slot = this.querySelector('[data-state-open]');
      slot.replaceChildren(card);
      this.show('open');

      this.querySelectorAll('.kc-btn-teal[data-turn]').forEach((button) => {
        if (button.dataset.again) button.textContent = button.dataset.again;
      });
    }

    addButton(item) {
      const labels = this.labels || {};
      if (!item.single || !item.variant) {
        const link = document.createElement('a');
        link.className = 'kc-og kc-og--sm';
        link.href = item.url;
        link.textContent = labels.options || 'Choose options';
        return link;
      }
      const wrap = document.createElement('add-to-cart');
      wrap.className = 'kc-add';
      wrap.dataset.variantId = item.variant;
      wrap.setAttribute('data-kc-add', '');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kc-og kc-og--sm';
      button.setAttribute('aria-label', `${labels.add || 'Add to cart'}: ${item.title}`);
      const label = document.createElement('span');
      label.className = 'kc-add__label';
      label.textContent = labels.add || 'Add to cart';
      const done = document.createElement('span');
      done.className = 'kc-add__done';
      done.textContent = labels.added || 'Added ✓';
      button.append(label, done);
      wrap.append(button);
      return wrap;
    }
  }

  customElements.define('kc-gacha', KcGacha);
}
